import { ReactComponent as LogoMark } from '../assets/logo.svg';
import { useBusinessSettings } from '../context/BusinessSettingsContext';

export default function Footer() {
  const { business, formatPrice } = useBusinessSettings();
  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

  return (
    <footer
      id="footer"
      style={{
        background: 'var(--dark)',
        color: 'rgba(255,255,255,0.68)',
        padding: '4rem 2rem 2rem',
      }}
    >
      <div style={{ maxWidth: 'var(--max-width)', margin: '0 auto' }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '2fr 1fr 1fr 1fr',
            gap: '3rem',
            marginBottom: '3rem',
          }}
          className="footer-grid"
        >
          {/* Brand */}
          <div>
            <div style={{ marginBottom: '.85rem' }}>
              <LogoMark
                aria-hidden="true"
                style={{ width: 56, height: 56, flexShrink: 0, color: 'white', display: 'block' }}
              />
            </div>
            <p style={{ fontSize: '.85rem', lineHeight: 1.7, marginBottom: '1.5rem', maxWidth: 300 }}>
              Where Exotic Flavors Meet Paradise. Authentic Thai cuisine, freshly made to order at our Beau Vallon kiosk.
            </p>
            <div style={{ display: 'flex', gap: '.7rem' }}>
              {[
                { icon: '📷', label: 'Instagram', href: 'https://instagram.com/thaiexpressseychelles' },
                { icon: '📘', label: 'Facebook',  href: 'https://facebook.com/thaiexpressseychelles' },
                { icon: '📞', label: 'Call us',   href: 'tel:+2482601234' },
              ].map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={s.label}
                  style={{
                    width: 40, height: 40,
                    borderRadius: '50%',
                    border: '1px solid rgba(255,255,255,0.18)',
                    background: 'none',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '.9rem',
                    textDecoration: 'none',
                    transition: 'all .2s',
                    color: 'rgba(255,255,255,0.7)',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--teal)'; e.currentTarget.style.borderColor = 'var(--teal)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.18)'; }}
                >
                  {s.icon}
                </a>
              ))}
            </div>
          </div>

          {/* Quick links */}
          <div>
            <h4 style={{ color: 'white', fontWeight: 600, fontSize: '.875rem', marginBottom: '1rem', textTransform: 'uppercase', letterSpacing: '.08em' }}>
              Quick Links
            </h4>
            <ul style={{ listStyle: 'none' }}>
              {[
                { label: 'Full Menu', id: 'menu' },
                { label: "Today's Specials", id: 'specials' },
                { label: 'Our Story', id: 'story' },
                { label: 'Reviews', id: 'feedback' },
              ].map((l) => (
                <li key={l.id} style={{ padding: '.3rem 0' }}>
                  <a
                    href={`#${l.id}`}
                    onClick={(e) => { e.preventDefault(); scrollTo(l.id); }}
                    style={{ color: 'rgba(255,255,255,0.6)', textDecoration: 'none', fontSize: '.85rem', transition: 'color .18s' }}
                    onMouseEnter={(e) => (e.target.style.color = 'var(--gold-light)')}
                    onMouseLeave={(e) => (e.target.style.color = 'rgba(255,255,255,0.6)')}
                  >
                    {l.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Hours */}
          <div style={{ textAlign: 'left' }}>
            <h4 style={{ color: 'white', fontWeight: 600, fontSize: '.875rem', marginBottom: '1rem', textTransform: 'uppercase', letterSpacing: '.08em' }}>
              Kiosk Hours
            </h4>
            <div
              className="footer-hours"
              style={{
                fontSize: '.85rem',
                lineHeight: 1.55,
                display: 'grid',
                gridTemplateColumns: 'auto 1fr',
                columnGap: '1rem',
                rowGap: '.4rem',
                justifyItems: 'start',
                alignItems: 'baseline',
              }}
            >
              <span style={{ color: 'rgba(255,255,255,0.5)', fontWeight: 500 }}>Monday</span>
              <span style={{ color: 'rgba(255,255,255,0.85)', fontWeight: 500 }}>Closed</span>
              <span style={{ color: 'rgba(255,255,255,0.5)', fontWeight: 500 }}>Tue – Sun</span>
              <span style={{ color: 'rgba(255,255,255,0.85)', fontWeight: 500 }}>12:00 – 21:00</span>
            </div>
          </div>

          {/* Contact */}
          <div>
            <h4 style={{ color: 'white', fontWeight: 600, fontSize: '.875rem', marginBottom: '1rem', textTransform: 'uppercase', letterSpacing: '.08em' }}>
              Contact
            </h4>
            <ul style={{ listStyle: 'none', fontSize: '.85rem' }}>
              <li style={{ padding: '.3rem 0' }}>📍 Beau Vallon, Mahé, Seychelles</li>
              <li style={{ padding: '.3rem 0' }}>
                <a href="tel:+2482601234" style={{ color: 'rgba(255,255,255,0.6)', textDecoration: 'none' }}>📞 +248 260 1234</a>
              </li>
              <li style={{ padding: '.3rem 0' }}>
                <a href="mailto:hello@thaiexpress.sc" style={{ color: 'rgba(255,255,255,0.6)', textDecoration: 'none' }}>✉️ hello@thaiexpress.sc</a>
              </li>
              <li style={{ padding: '.75rem 0 .3rem', color: 'rgba(255,255,255,0.35)', fontSize: '.78rem' }}>
                Minimum order: {formatPrice(business.minimumOrderAmount)}
              </li>
              <li style={{ color: 'rgba(255,255,255,0.35)', fontSize: '.78rem' }}>
                Kiosk pickup only
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div
          className="footer-bottom-bar"
          style={{
            borderTop: '1px solid rgba(255,255,255,0.1)',
            paddingTop: '1.5rem',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '1rem',
            fontSize: '.78rem',
          }}
        >
          <span className="footer-bottom-copy">2026 Thai Express Seychelles, thaiexpress.sc. All rights reserved.</span>
          <div className="footer-legal-btns" style={{ display: 'flex', gap: '1.5rem' }}>
            {['Privacy Policy', 'Terms of Service', 'Allergen Info'].map((l) => (
              <button
                key={l}
                type="button"
                className="footer-legal-btn"
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'default',
                  color: 'rgba(255,255,255,0.35)',
                  textDecoration: 'none',
                  transition: 'color .18s',
                  font: 'inherit',
                  fontSize: 'inherit',
                  padding: '0.35rem 0',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.color = 'rgba(255,255,255,0.65)')}
                onMouseLeave={(e) => (e.currentTarget.style.color = 'rgba(255,255,255,0.35)')}
              >
                {l}
              </button>
            ))}
          </div>
        </div>
      </div>

      <style>{`
        @media (max-width: 900px) {
          .footer-grid { grid-template-columns: 1fr 1fr !important; }
        }
        @media (max-width: 560px) {
          .footer-grid { grid-template-columns: 1fr !important; }
          .footer-bottom-bar {
            flex-direction: column !important;
            align-items: center !important;
            text-align: center;
          }
          .footer-bottom-copy { width: 100%; max-width: 320px; }
          .footer-legal-btns {
            flex-direction: column !important;
            align-items: center !important;
            width: 100%;
            max-width: 280px;
            gap: 0.5rem !important;
          }
          .footer-legal-btn {
            width: 100% !important;
            max-width: 280px;
            text-align: center;
            border-radius: 8px;
            padding: 0.55rem 0.75rem !important;
            border: 1px solid rgba(255,255,255,0.12) !important;
          }
        }
      `}</style>
    </footer>
  );
}
